import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Stack, Button, Container } from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { useFormContext } from '../../hooks/useForm';
import SectionPanel from './formViewer/SectionPanel';

FormPreview.propTypes = {
  disabled: PropTypes.bool,
  addTicket: PropTypes.func,
  handleBackdropOpen: PropTypes.func,
  handleBackdropClose: PropTypes.func,
  formSharableSettings: PropTypes.object,
};

export default function FormPreview({
  disabled,
  addTicket,
  handleBackdropOpen,
  handleBackdropClose,
  formSharableSettings,
}) {
  const navigate = useNavigate();
  const { id, isAuthenticated } = useAuth();
  const {
    getOrderedSections,
    updateSectionValidity,
    getSectionQuestions,
    getFormData,
    formatAttachements,
  } = useFormContext();
  const [sectionsList, setSections] = useState([]);
  const [submitSection, setSubmitSection] = useState(false);
  const [page, setPage] = useState(0);
  const { showToast, hideToast } = useToast();
  const [formData, setFormData] = useState([]);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [hasSelectedSection, setHasSelectedSection] = useState(false);
  const [searchParams] = useSearchParams();
  const formToken = searchParams.get('form');
  const sectionsPerPage = 1;
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    const now = new Date();
    setStartTime(now);

    const updateSections = getOrderedSections();
    setSections(updateSections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormDataUpdate = () => {
    const updatedFormData = [
      ...formData,
      {
        sectionId: sectionsList[page].id,
        sectionName: sectionsList[page].name,
        questions: getSectionQuestions(sectionsList[page].id),
      },
    ];
    setFormData(updatedFormData);
    return updatedFormData;
  };

  const submitTicket = () => {
    const isValid = updateSectionValidity(sectionsList[page].id);
    if (!isValid) {
      showToast({
        message: t('formControl.makeSureAllFields'),
        severity: 'error',
        props: { hideToast },
      });
      return;
    }
    const endTime = new Date().toISOString();
    const attachements = formatAttachements();
    handleBackdropOpen();
    const updatedData = handleFormDataUpdate();
    const dt = getFormData();
    const { priority, duration, category, agents, autoAssign, tags } = sectionsList[page];

    if (!isDebouncing) {
      setIsDebouncing(true);

      addTicket(
        id,
        updatedData,
        priority,
        duration,
        category,
        attachements,
        dt.formData.ticketDescription,
        dt.formData.department,
        agents,
        autoAssign,
        tags,
        startTime,
        endTime,
        formSharableSettings.isHidden,
        tags
      )
        .then(() => {
          handleBackdropClose();
          if (isAuthenticated) {
            if (formSharableSettings.isHidden !== true) {
              navigate('/dashboard/tickets');
              showToast({
                message: t('successMessages.successTicketAdd'),
                severity: 'success',
                props: { hideToast },
              });
            } else {
              navigate('/dashboard/shareableForms');
            }
          } else {
            navigate('/form-sharable-submitted', {
              state: {
                isMultipleSubmission: formSharableSettings.isMultipleSubmission,
                isAnonymous: formSharableSettings.isAnonymous,
              },
            });
          }
        })
        .catch((error) => {
          handleBackdropClose();
          showToast({
            message:
              error.response.status === 409
                ? t('errorMessages.alreadySubmittedTheForm')
                : t('errorMessages.errorCreatingTicket'),
            severity: 'error',
            props: { hideToast },
          });
        });
      setTimeout(() => {
        setIsDebouncing(false);
      }, 300);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth', // Add smooth scrolling animation
    });
  };

  const handleNextPage = () => {
    const isValid = updateSectionValidity(sectionsList[page].id);

    if (!isValid) {
      showToast({
        message: t('formControl.makeSureAllFields'),
        severity: 'error',
        props: { hideToast },
      });
      return;
    }
    handleFormDataUpdate();
    setPage((prevPage) => prevPage + 1);
    scrollToTop();
  };

  const handlePrevPage = () => {
    const updatedFormData = formData.slice(0, -1);
    setFormData(updatedFormData);
    setPage((prevPage) => prevPage - 1);
    scrollToTop();
  };
  const totalSections = sectionsList.length;

  useEffect(() => {
    const submit = sectionsList[page]?.nextSection === 'submit' && !hasSelectedSection;
    setSubmitSection(submit);
  }, [page, sectionsList, hasSelectedSection]);

  return (
    <div>
      {sectionsList
        ?.slice(page * sectionsPerPage, (page + 1) * sectionsPerPage)
        .map((section, index) => (
          <SectionPanel
            key={index}
            section={section}
            sectionsList={sectionsList}
            page={page}
            setSections={setSections}
            setPage={setPage}
            setHasSelectedSection={setHasSelectedSection}
            hasSelectedSection={hasSelectedSection}
          />
        ))}

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{ mt: isSmallScreen ? 3 : 5 }}
          spacing={2}
        >
          {page > 0 && (
            <Button
              id="last-section-btn"
              ariant="contained"
              color="primary"
              onClick={handlePrevPage}
              size={isSmallScreen ? 'small' : 'medium'}
              sx={{ fontSize: isSmallScreen ? '0.775rem' : {} }}
            >
              {t('buttons.back')}
            </Button>
          )}
          {page < Math.ceil(totalSections / sectionsPerPage) - 1 &&
            (hasSelectedSection || sectionsList[page]?.nextSection === undefined) && (
              <Button
                id="next-section-btn"
                variant="contained"
                color="primary"
                onClick={handleNextPage}
                size={isSmallScreen ? 'small' : 'medium'}
                sx={{ fontSize: isSmallScreen ? '0.775rem' : {} }}
              >
                {t('buttons.next')}
              </Button>
            )}
          {(page + 1 === sectionsList.length || submitSection) && (
            <Button
              id="submit-form-btn"
              variant="contained"
              sx={{
                background:
                  !formToken && isAuthenticated
                    ? '#9c27b0'
                    : 'linear-gradient(135deg, #56b499, #e2dca5)',
                width: isSmallScreen ? '120px' : '150px',
                fontSize: isSmallScreen ? '0.775rem' : {},
              }}
              size={isSmallScreen ? 'small' : 'medium'}
              onClick={submitTicket}
              disabled={disabled || isDebouncing}
            >
              {t('buttons.submit')}
            </Button>
          )}
        </Stack>
      </Container>
    </div>
  );
}
